<template>
  <div
    class="box columns is-gapless assets-offline-map-import"
    :style="{ height: `${height}px` }"
  >
    <div class="column is-4">

      <div class="card">
        <header class="card-header">
          <p class="card-header-title is-uppercase">Available For Import</p>
          <div class="buttons">
            <b-upload accept=".json,.csv" v-model="file" @input="onFileInput">
                <b-button tag="a" type="is-default" size="is-small" :loading="uploading">UPLOAD</b-button>
              </b-upload>
          </div>
        </header>

        <div class="card-content">

          <div v-if="uploadProgress.loaded" style="margin-bottom: 1rem;">
            <p class="has-text-danger">
              Navigating away will abort upload!
              <br>
              Switching tabs will not abort upload.
            </p>
            <p v-if="uploadProgress.loaded" style="max-width: 220px;">
              Progress:
              <span>{{ uploadProgress.loaded | humanFileSize }}</span>
              <strong>
                (<span v-html="((uploadProgress.loaded / uploadProgress.total) * 100).toFixed(0)"></span>%)
              </strong>
            </p>
          </div>

          <div class="table-container scrollable afPanel">
            <b-loading :is-full-page="false" :active.sync="fetching" />
            <table v-if="availableFiles.length > 0" class="table is-fullwidth">
              <tbody>
                <tr v-for="file in availableFiles" :key="file.id">
                  <td>
                    {{ friendlyFilename(file) }}
                  </td>
                  <td class="nowrap">
                    <template v-if="file.id != selected_for_import">
                      <button class="button is-default is-small"
                        @click="onSelectForImport(file)">
                        IMPORT
                      </button>
                      <button class="button is-danger is-small"
                        @click="onRemove(file)"
                          :disabled="removing">
                          DELETE
                      </button>
                    </template>
                    <template v-else>
                      <span class="tag is-info">Currently Importing</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="has-text-grey">No files found.</p>
          </div>
        </div>
      </div>

    </div> <!-- .column -->

    <div class="column is-4">

      <div class="card selected-for-import">
        <header class="card-header">
          <p class="card-header-title is-uppercase">File Currently Importing</p>
        </header>
        <div class="card-content">
          <div v-if="selected_for_import" class="has-text-centered">
            <p v-if="fileSelectedForImport">
              <strong>{{ friendlyFilename(fileSelectedForImport) }}</strong>
            </p>
            <div class="columns is-multiline">
              <div v-if="! anyImportProcessing || processingForm.asset_code_id != ''" class="column is-half">
                <p>
                  <span>Asset Class</span>
                </p>
                <div class="select">
                  <select v-model="processingForm.asset_code_id"
                    :class="{ 'is-loading': asset_codes_busy}"
                    :disabled="asset_codes_busy || anyImportProcessing">
                    <option :value="''">Please Select</option>
                    <option v-for="code in asset_codes" :value="code.id" :key="code.id">{{ `${code.code} - ${code.name}` }}</option>
                  </select>
                </div>
                <p class="has-text-grey is-size-7">Selection applies to entire import.</p>
              </div>
              <div v-if="! anyImportProcessing || processingForm.asset_code_id == ''" class="column is-half">
                <p><span>Asset Class Key</span></p>
                <input type="text" class="input"
                  v-model="processingForm.asset_class_key"
                  :disabled="processingForm.asset_code_id || anyImportProcessing"
                >
                <p class="has-text-grey is-size-7">Key to search if no Asset Class specified.</p>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Status Key</span></p>
                <input type="text" class="input"
                  v-model="processingForm.status_key"
                  :disabled="anyImportProcessing"
                >
                <p class="has-text-grey is-size-7">Key to search for status.</p>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Invalid Statuses</span></p>
                <base-tag-input
                  :attach-mode="true"
                  :value.sync="processingForm.invalid_statuses"
                  label
                  placeholder="Add status"
                  :model-type="processingForm.contract_type_tag_id ? 'tag' : 'contract'"
                  :model-id="processingForm.contract_type_tag_id || processingForm.contract_id"
                  tag-type="[ASSET][INVALID_STATUS]"
                  :fetch-params="{
                    type: '[ASSET][INVALID_STATUS]',
                  }"
                  :allow-new="true"
                  :disabled="!processingForm.contract_id && !processingForm.contract_type_tag_id"
                  ref="tagInputInvalidStatuses"
                />
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Contract Type</span></p>
                <base-select
                  :service="contractService"
                  fetch-func="listTypes"
                  label
                  default-option-label="Not applicable"
                  :value.sync="processingForm.contract_type_tag_id"
                  :disabled="!!processingForm.contract_id"
                />
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Third Party Code Key</span></p>
                <input type="text" class="input"
                  v-model="processingForm.third_party_code_key"
                  :disabled="anyImportProcessing"
                >
                <p class="has-text-grey is-size-7">Key to search for populating third_party_code.</p>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Assign Assets to Contracts</span></p>
                <br />
                <div class="block">
                  <b-radio v-model="processingForm.assign_to_contracts" :native-value="true" :disabled="!!processingForm.contract_id">
                    Yes
                  </b-radio>
                  <b-radio v-model="processingForm.assign_to_contracts" :native-value="false" :disabled="!!processingForm.contract_id">
                    No
                  </b-radio>
                </div>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Parent Third Party Code Key</span></p>
                <input type="text" class="input"
                  v-model="processingForm.parent_third_party_code_key"
                  :disabled="anyImportProcessing"
                >
                <p class="has-text-grey is-size-7">Key to search for populating parent asset.</p>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Contract</span></p>
                <base-select
                  :service="contractService"
                  fetch-func="all"
                  :fetch-params="{
                    includes: 'contractor',
                  }"
                  label
                  default-option-label="Not applicable"
                  :value.sync="processingForm.contract_id"
                  :disabled="!!processingForm.contract_type_tag_id"
                >
                  <template #customLabel="{ option }">
                    {{ `${option.contractor.name} - ${option.name}` }}
                  </template>
                </base-select>
              </div>
              <div v-if="! anyImportProcessing" class="column is-half">
                <p><span>Order Type</span></p>
                <base-select
                  :service="codeService"
                  fetch-func="all"
                  :fetch-params="{
                    type: 'orderType',
                    contract_id: processingForm.contract_id,
                  }"
                  :fetch-on-init="false"
                  label
                  :value.sync="processingForm.orderType_code_id"
                  preselect-on-one
                  :disabled="!processingForm.contract_id"
                  ref="selectOrderType"
                >
                </base-select>
              </div>
              <div class="column is-half">
                <p><span>Duplicate Asset Treatment</span></p>
                <br />
                <div class="block">
                  <b-radio v-model="processingForm.duplicate_treatment" native-value="skip" :disabled="anyImportProcessing">
                    Skip
                  </b-radio>
                  <b-radio v-model="processingForm.duplicate_treatment" native-value="replace">
                    Replace
                  </b-radio>
                </div>
              </div>
            </div>
            <p>
              <span>Imported: </span>
              <span class="has-text-grey">{{ status.processed }}</span>
            </p>
            <p v-if="file_ids_processed.indexOf(selected_for_import) >= 0">
              <span class="has-text-info">Import complete.</span>
              <br>
              <button class="button is-info"
                @click="onDismissProcessedImport()">
                DISMISS
              </button>
            </p>
            <p v-else>
              <span v-if="anyImportProcessing && file_ids_processing.indexOf(selected_for_import) < 0" class="has-text-danger">
                Another import is currently in progress.
              </span>
              <button v-else class="button is-info"
                :class="{ 'is-loading': anyImportProcessing }"
                @click="onProcess(fileSelectedForImport)"
                :disabled="anyImportProcessing">
                PROCESS
              </button>
            </p>
          </div>
          <p v-else class="has-text-grey">No file selected for import.</p>
        </div>
      </div>

    </div> <!-- .column -->

    <div class="column is-4">

      <div class="card" style="height: 100%;">

        <header class="card-header">

          <p class="card-header-title is-uppercase">Imported Files</p>

          <div class="buttons">
              <b-field>
                  <b-button :loading="reporting" type="is-default" size="is-small" @click="report">IMPORT REPORT</b-button>
              </b-field>
          </div>

        </header>

        <div class="card-content" style="height: calc(100% - 36px);">
          <div class="table-container scrollable" style="height: 100%;">
            <table v-if="reversibleFiles.length > 0" class="table is-fullwidth">
                <tbody>
                  <tr v-for="file in reversibleFiles" :key="file.id">
                    <td>
                      <!-- <span class="has-text-grey">
                        {{ friendlyFilename(file) }}
                      </span> -->
                      <b-button type="is-text" tag="a" :href="file.path" target="_blank">
                        {{ friendlyFilename(file) }}
                      </b-button>
                      <b-tooltip :label="failedFileMsg(file)" type="is-dark" position="is-right" v-if="file.status.toUpperCase() == 'FAILED'">
                        <span v-if="file.status.toUpperCase() == 'FAILED'" class="tag is-danger">FAILED</span>
                      </b-tooltip>
                    </td>
                    <td class="nowrap">
                      <b-button
                        size="is-small"
                        :loading="reversing"
                        :disabled="reversing"
                        @click="onReverse(file.id)"
                        >REVERSE</b-button
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="has-text-grey-light">No files available.</p>
          </div>
        </div>
      </div>

    </div> <!-- .column -->

  </div>
</template>

<script>
import moment from "moment";
import BaseSelect from "@/components/partials/BaseSelect";
import BaseTagInput from "@/components/partials/BaseTagInput";
import { ApiUtil as api } from "@/utils/api.util.js";
import { AssetService } from "@/services/asset.service";
import { CodeService } from "@/services/code.service";
import { ContractService } from "@/services/contract.service";
import { TagService } from "@/services/tag.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

export default {
  props: {
    mapHeight: {
      type: Number,
      default: 500
    }
  },
  components: {
    BaseSelect,
    BaseTagInput,
  },
  data: () => ({
    assetService: AssetService,
    codeService: CodeService,
    contractService: ContractService,
    tagService: TagService,
    asset_codes: [],
    asset_codes_busy: false,
    file: null,
    files: [],
    uploadProgress: {
      loaded: 0,
      total: 0
    },
    status: {
      id: null,
      status: null,
      processed: 0 // assets processed in file (status check)
    },
    checking: false,
    fetching: false,
    uploading: false,
    removing: false,
    reporting: false,
    reversing: false,
    selected_for_import: null,
    import_progress_interval: null,
    file_ids_processing: [],
    file_ids_processed: [],
    processingForm: {
      contract_type_tag_id: '',
      assign_to_contracts: '',
      contract_id: '',
      orderType_code_id: '',
      asset_code_id: '', // if entire import should use same asset class code
      asset_class_key: '', // if server should look in JSON node for asset class code
      status_key: 'SW_STATUS',
      invalid_statuses: [],
      third_party_code_key: 'SW_SAP_ID',
      parent_third_party_code_key: 'PARENT_ID',
      duplicate_treatment: 'skip', // if server should skip or replace duplicate assets
    }
  }),
  computed: {
    height() {
      return this.mapHeight;
    },
    fileSelectedForImport() {
      let files = this.files.filter(file => {
        return file.id == this.selected_for_import;
      });
      return files.length ? files.shift() : null;
    },
    availableFiles() {
      return this.files.filter(file =>
        ["uploaded", "downloading", "processing"].includes(file.status)
      );
    },
    reversibleFiles() {
      let eligible = this.files.filter(file =>
        ["failed", "processed"].includes(file.status)
      );
      return eligible;
    },
    anyImportProcessing() {
      return this.file_ids_processing.length > 0;
    }
  },
  watch: {
    "processingForm.contract_type_tag_id"() {
      this.$nextTick(() => {
        // this.$refs.tagInputInvalidStatuses.fetchTags();
        this.fetchInvalidStatues();
        // Reset contract
        if (this.processingForm.contract_type_tag_id) {
          this.processingForm.contract_id = "";
        }
      });
    },
    "processingForm.contract_id"() {
      this.$nextTick(() => {
        this.$refs.selectOrderType.fetchOptions();
        // this.$refs.tagInputInvalidStatuses.fetchTags();
        this.fetchInvalidStatues();
        // Reset contract type
        if (this.processingForm.contract_id) {
          this.processingForm.contract_type_tag_id = "";
        }
      });
    },
    'files': function(nextList) {
      let file_ids = [];
      let file_ids_processing = [];
      let selected_for_import = null;
      nextList.forEach(file => {
        file_ids.push(file.id);
        if (file.status.toUpperCase() == 'PROCESSING') {
          file_ids_processing.push(file.id);
          selected_for_import = file.id;
        }
      });
      this.file_ids_processing = file_ids_processing;
      if (selected_for_import && ! this.selected_for_import) {
        this.selected_for_import = selected_for_import;
      }
      // If no file is currently processing, then clear out the last known
      // asset_code_id since the user may pick a totally unrelated file.
      if ( ! selected_for_import) {
        localStorage.setItem("__asset_import_code_id", '');
        this.resetProcessingForm();
      }
    },
    'selected_for_import': function(next_id, prev_id) {
      // This value will be updated by polling AJAX soon enough.
      this.$set(this.status, 'processed', 0);
      this.resetProcessingForm();
    }
  },
  beforeDestroy() {
    if (this.import_progress_interval) {
      window.clearInterval(this.import_progress_interval);
    }
  },
  async mounted() {
    this.fetch();
    this.fetchAssetCodes();
    this.resetProcessingForm();
    this.import_progress_interval = window.setInterval(this.checkStatus, 5000);
  },
  methods: {
    resetProcessingForm() {
      this.processingForm = {
        contract_type_tag_id: localStorage.getItem("__asset_import_contract_type_tag_id") || '',
        assign_to_contracts: localStorage.getItem("__asset_import_assign_to_contracts") || '',
        contract_id: localStorage.getItem("__asset_import_contract_id") || '',
        orderType_code_id: localStorage.getItem("__asset_import_orderType_code_id") || '',
        asset_code_id: localStorage.getItem("__asset_import_code_id") || '',
        asset_class_key: localStorage.getItem("__asset_import_code_key") || "SW_OBJECTTYPE",
        status_key: localStorage.getItem("__asset_import_status_key") || "SW_STATUS",
        // invalid_statuses: localStorage.getItem("__asset_import_invalid_statuses") ? JSON.parse(localStorage.getItem("__asset_import_invalid_statuses")) : [],
        third_party_code_key: localStorage.getItem("__asset_import_third_party_code_key") || "SW_SAP_ID",
        parent_third_party_code_key: localStorage.getItem("__asset_import_parent_third_party_code_key") || "PARENT_ID",
        duplicate_treatment: localStorage.getItem("__asset_import_duplicate_treatment") || "skip"
      };
    },
    async fetch() {
      this.fetching = true;

      let files = await this.assetService
        .listImports({ includes: "assetImportFailedLogs" })
        .then(response => JsonDenormalizer.denormalize(response));

      // Newest files at the top
      files.sort((a, b) => {
        return a.id > b.id ? -1 : 1;
      });
      this.files = files;

      this.fetching = false;
    },
    async fetchAssetCodes() {
      this.asset_codes_busy = true;
      this.asset_codes = await this.codeService
        .all({ type: 'ASSET' })
        .then(response => JsonDenormalizer.denormalize(response));
      this.asset_codes_busy = false;
    },
    friendlyFilename(file) {
      /**
       *  @todo - Tidy up filenames when downloaded from s3 server side.
       *  (files seem to be keeping the URL signature in the filename)
       */
      let filename = file.filename.split('?')[0].split('/').pop();
      return filename;
    },
    failedFileMsg(file) {
      const logs = (file.assetImportFailedLogs || []).filter(v => v.subtype == 'ASSET_IMPORTER::FAILED');
      return logs.length ? logs[0].content : '';
    },
    onSelectForImport(file) {
      if (this.selected_for_import == file.id) {
        this.selected_for_import = null;
      }
      else this.selected_for_import = file.id;
    },
    async upload(file) {
      this.uploading = true;

      try {
        const response = await this.assetService.uploadImport({
          filename: file.name
        });

        await api.put(response.url, file, {
          onUploadProgress: this.onUploadProgress
        });
        await this.assetService.createImport({ filename: response.filename });

        this.uploadProgress.loaded = 0;
        this.uploadProgress.total = 0;
        this.file = null;
        this.fetch();

        this.$buefy.toast.open({
          message: "File has been uploaded.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}

      this.uploading = false;
    },
    async report() {
      this.reporting = true;

      try {
        const data = await this.assetService.importReport(
          {},
          { responseType: "blob" }
        );

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(data);
        link.download = `assets_import_report_${moment().format(
          "MM-DD-YYYY"
        )}.csv`;
        link.click();
      } catch (err) {}

      this.reporting = false;
    },
    onFileInput(file) {
      if (file) {
        this.upload(file);
      }
    },
    onUploadProgress(event) {
      this.uploadProgress.loaded = event.loaded;
      this.uploadProgress.total = event.total;
    },
    onProcess() {
      // Used to trigger a dialog asking for asset class key to search
      // (now we have form elements to enter this data before submit)
      this.process();
    },
    process() {

      let payload = Object.assign(
        { file_id: this.fileSelectedForImport.id },
        this.processingForm
      );

      if (payload.asset_code_id) {
        delete payload.asset_class_key;
      }
      else delete payload.asset_code_id;

      if ( ! payload.asset_code_id && ! payload.asset_class_key) {
        return false;
      }

      localStorage.setItem("__asset_import_code_id", this.processingForm.asset_code_id || '');
      localStorage.setItem("__asset_import_code_key", this.processingForm.asset_class_key);
      localStorage.setItem("__asset_import_status_key", this.processingForm.status_key);
      // localStorage.setItem("__asset_import_invalid_statuses", JSON.stringify(this.processingForm.invalid_statuses || []));
      localStorage.setItem("__asset_import_third_party_code_key", this.processingForm.third_party_code_key);
      localStorage.setItem("__asset_import_parent_third_party_code_key", this.processingForm.parent_third_party_code_key);
      localStorage.setItem("__asset_import_duplicate_treatment", this.processingForm.duplicate_treatment);

      this.toggleProcessing(this.fileSelectedForImport.id);

      this.assetService.instantProcessImport({
        ...payload,
        assign_to_contracts: payload.assign_to_contracts ? 1 : 0,
      })
      .then(() => {
        this.toggleProcessing(this.fileSelectedForImport.id);
        // No, don't deselect the chosen import since we
        // have an import progress report (via polling)
        // this.selected_for_import = null;
        this.$buefy.toast.open({
          message: "File has been queued for processing.",
          type: "is-info",
          position: "is-top-right"
        });
      })
      .catch(() => {
        this.toggleProcessing(this.fileSelectedForImport.id);
        // this.fetch();
      });
    },
    toggleProcessing(file_id) {
      if (this.file_ids_processing.indexOf(file_id) >= 0) {
        this.file_ids_processing = this.file_ids_processing.filter(file => {
          return file.id != file_id;
        });
      }
      else {
        this.file_ids_processing.push(file_id);
      }
    },
    async checkStatus() {
      if (!this.selected_for_import
          || this.file_ids_processing.indexOf(this.selected_for_import) < 0
          || this.file_ids_processed.indexOf(this.selected_for_import) >= 0) {
        this.checking = false;
        return;
      }

      this.checking = true;
      try {
        const status = await this.assetService
          .statusImport({
            filename: this.fileSelectedForImport.filename
          })
          .then(response => response.data);

        this.checking = false;

        // If the same file is still selected, update the progress details.
        if (this.selected_for_import == status.id) {
          this.status = status;
        }

        if (['PROCESSED', 'FAILED'].indexOf(status.status.toUpperCase()) >= 0) {
          this.$buefy.toast.open({
            message: "Import finished processing.",
            type: "is-info",
            position: "is-top-right"
          });
          // If the completed import check is for the currently selected
          // file then we reset some values ready for another import.
          if (this.selected_for_import == status.id) {
            this.checking = false;
            this.toggleProcessing(status.id);
            this.file_ids_processed.push(parseInt(status.id, 10).toString());
            // Don't deselect the import since we
            // have a DISMISS button available
            // this.selected_for_import = null;
            // this.fetch();
          }
        }
      } catch (err) {
        this.checking = false;
      }
    },
    async fetchInvalidStatues() {
      if (
        !this.processingForm.contract_id &&
        !this.processingForm.contract_type_tag_id
      ) {
        this.$set(this.processingForm, "invalid_statuses", []);
        return;
      }
      try {
        let tags = [];

        if (this.processingForm.contract_type_tag_id) {
          const contractTypeTag = await this.tagService
            .show(this.processingForm.contract_type_tag_id, {
              includes: "tags"
            })
            .then(response => JsonDenormalizer.denormalize(response));
          tags = contractTypeTag.tags || [];
        } else if (this.processingForm.contract_id) {
          const contract = await this.contractService
            .show(this.processingForm.contract_id, { includes: "tags" })
            .then(response => JsonDenormalizer.denormalize(response));
          tags = contract.tags || [];
        }

        this.$set(
          this.processingForm,
          "invalid_statuses",
          tags.map(tag => tag.name)
        );
      } catch (err) {}
    },
    async onRemove(file) {
      const vm = this;
      vm.removing = true;
      try {
        await this.assetService.deleteImport({ filename: file.filename });
        vm.removing = false;
        vm.$buefy.toast.open({
          message: "File has been deleted.",
          type: "is-info",
          position: "is-top-right"
        });
        vm.fetch();
      } catch (err) {
        vm.removing = false;
        vm.$buefy.toast.open({
          message: "Failed to delete file.",
          type: "is-danger",
          position: "is-top-right"
        });
      }
    },
    onReverse(file_id) {
      const vm = this;
      const file = vm.reversibleFiles.find(f => {
        return f.id == file_id;
      });
      if (file) {
        vm.reversing = true;
        vm.assetService.reverseImport(file.id)
          .then(() => {
            vm.$buefy.toast.open({
              message: "Import reversed.",
              type: "is-success",
              position: "is-top-right"
            });
            vm.reversing = false;
            vm.fetch();
          })
          .catch(() => {
            vm.reversing = false;
          });
      }
    },
    async onDismissProcessedImport() {
      this.selected_for_import = null;
      // The last selected asset_code_id may not be relevant for future files.
      localStorage.setItem("__asset_import_code_id", '');
      this.resetProcessingForm();
      await this.fetch();
    }
  }
};
</script>

<style lang="scss">
.nowrap { white-space: nowrap; }
.assets-offline-map-import {
  .afPanel {
    position: relative;

    .loading-overlay {
      z-index: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AssetsOfflineMapImport.vue